// Utility functions for proposal calculations and formatting

export interface ProposalWithWeighted {
  id: string;
  title: string;
  value: number | null;
  final_value: number | null;
  probability: number | null;
  expected_invoicing_date: string | null;
  weighted_value: number;
  effective_value: number; // final_value || value
}

// Calculate weighted value for a proposal
export const calculateWeightedValue = (amount: number | null, probability: number | null): number => {
  if (!amount || !probability) return 0;
  return Math.round((amount * probability / 100) * 100) / 100;
};

// Get the effective proposal value (final_value takes precedence over value)
export const getEffectiveValue = (proposal: { value: number | null; final_value: number | null }): number => {
  return proposal.final_value || proposal.value || 0;
};

// Enhance proposal with calculated fields
export const enhanceProposal = (proposal: any): ProposalWithWeighted => {
  const effectiveValue = getEffectiveValue(proposal);
  const weightedValue = calculateWeightedValue(effectiveValue, proposal.probability);
  
  return {
    ...proposal,
    effective_value: effectiveValue,
    weighted_value: weightedValue,
  };
};

// Calculate total weighted pipeline value from proposals array
export const calculateTotalWeightedValue = (proposals: any[]): number => {
  return proposals.reduce((total, proposal) => {
    const effectiveValue = getEffectiveValue(proposal);
    const weightedValue = calculateWeightedValue(effectiveValue, proposal.probability);
    return total + weightedValue;
  }, 0);
};

// Group proposals by expected invoicing month
export const groupProposalsByMonth = (proposals: any[]): Record<string, ProposalWithWeighted[]> => {
  const grouped: Record<string, ProposalWithWeighted[]> = {};
  
  proposals.forEach(proposal => {
    if (proposal.expected_invoicing_date) {
      const month = proposal.expected_invoicing_date.substring(0, 7); // YYYY-MM format
      const enhanced = enhanceProposal(proposal);
      
      if (!grouped[month]) {
        grouped[month] = [];
      }
      grouped[month].push(enhanced);
    }
  });
  
  return grouped;
};

// Calculate monthly weighted totals
export const calculateMonthlyWeightedTotals = (proposals: any[]): Record<string, number> => {
  const grouped = groupProposalsByMonth(proposals);
  const totals: Record<string, number> = {};
  
  Object.entries(grouped).forEach(([month, monthlyProposals]) => {
    totals[month] = calculateTotalWeightedValue(monthlyProposals);
  });
  
  return totals;
};

// Format currency for display
export const formatCurrency = (amount: number, currency: string = 'EUR'): string => {
  return new Intl.NumberFormat('el-GR', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

// Get probability color for UI
export const getProbabilityColor = (probability: number | null): string => {
  if (!probability) return 'text-muted-foreground';
  
  if (probability >= 90) return 'text-green-600';
  if (probability >= 70) return 'text-blue-600';
  if (probability >= 50) return 'text-yellow-600';
  if (probability >= 30) return 'text-orange-600';
  return 'text-red-600';
};

// Get probability badge variant
export const getProbabilityVariant = (probability: number | null): "default" | "secondary" | "destructive" | "outline" => {
  if (!probability) return 'outline';
  
  if (probability >= 90) return 'default'; // Green-ish
  if (probability >= 70) return 'secondary'; // Blue-ish
  if (probability >= 50) return 'outline'; // Yellow-ish
  return 'destructive'; // Red-ish
};